/*
 * ObligacionesAdicionales.js — Pestaña "Obligaciones adicionales" (C-021, ver diseno/CAMBIOS.md).
 *
 * Una sección por obligación (IMSS, nómina trimestral, hospedaje) con solo los clientes que la
 * tienen; columnas Envío al cliente → Pago, en cascada. La lógica pura está en obligaciones.js.
 *
 * props:
 *   periodo           { mes: 1-12, anio } — el mes que se declara (el de la barra)
 *   api               { listar(mes, anio), guardar(clienteId, procesoId, estado, mes, anio),
 *                       soloLectura(), avisarSoloLectura() }
 *   esContador        sin selector de contador (solo ve sus clientes)
 *   soloAtrasadas     abre con el chip "Atrasadas" puesto (viene del aviso de la vista mensual)
 *   onCambio          (obligaciones) => {} para que la vista mensual actualice sus avisos
 *   Icono             componente del ícono del círculo (IconoEstado de App.js)
 *   busquedaInicial   texto para buscar al abrir (desde la ficha)
 *   onAbrirCliente    (id) => abre la ficha · onEditarCliente (id) => Editar cliente (solo Dueño y Supervisor)
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronDown, Search } from 'lucide-react';
import CeldaCliente from './CeldaCliente';
import { avisarError } from './Dialogo';
import { NOMBRES_ESTADO, fechaCorta, normalizarNombre } from './estados';
import {
  OBLIGACIONES, estadoObligacion, fechaLimiteObligacion, siguienteEstadoObligacion, textoAvance,
  textoNoToca, textoPeriodo,
} from './obligaciones';

const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];

export default function ObligacionesAdicionales({
  periodo, api, esContador, soloAtrasadas = false, onCambio, Icono, onAbrirCliente, onEditarCliente,
  busquedaInicial = '',
}) {
  const [datos, setDatos] = useState(null);
  const [contador, setContador] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [filtroAtrasadas, setFiltroAtrasadas] = useState(soloAtrasadas);
  const [elegida, setElegida] = useState(null); // clave de la obligación elegida, o null = Todas
  const [cerradas, setCerradas] = useState({});
  const hoy = new Date();
  const { mes, anio } = periodo;

  useEffect(() => { setFiltroAtrasadas(soloAtrasadas); }, [soloAtrasadas]);
  useEffect(() => { setBusqueda(busquedaInicial); }, [busquedaInicial]); // "Ver en la tabla →" de la ficha (C-023)

  const cargar = useCallback(async () => {
    try {
      const res = await api.listar(mes, anio);
      setDatos(res.obligaciones || []);
      onCambio && onCambio(res.obligaciones || []);
    } catch (err) {
      setDatos([]);
      avisarError('cargar las obligaciones adicionales', err);
    }
  }, [api, mes, anio, onCambio]);

  useEffect(() => { setDatos(null); cargar(); }, [cargar]);

  const contadores = useMemo(() => {
    const vistos = new Map();
    (datos || []).forEach(o => o.clientes.forEach(c => { if (c.contador_id) vistos.set(String(c.contador_id), c.contador_nombre); }));
    return [...vistos.entries()].sort((a, b) => (a[1] || '').localeCompare(b[1] || ''));
  }, [datos]);

  const q = normalizarNombre(busqueda.trim());
  const delContador = (c) => !contador || String(c.contador_id) === contador;
  const coincide = (c) => delContador(c) && (!q || normalizarNombre(c.nombre).includes(q));
  const estado = (c) => estadoObligacion(c, periodo, hoy);

  const secciones = (datos || []).map(o => {
    const def = OBLIGACIONES.find(x => x.clave === o.clave) || { nombre: o.nombre, corto: o.nombre };
    const todos = o.clientes.filter(delContador);
    const atrasados = o.toca ? todos.filter(c => estado(c).atrasado).length : 0;
    const visibles = o.clientes.filter(c => coincide(c) && (!filtroAtrasadas || estado(c).atrasado));
    return { ...o, def, todos, atrasados, visibles };
  });
  const totalAtrasadas = secciones.reduce((s, x) => s + x.atrasados, 0);

  const cambiar = async (obligacion, cliente, paso) => {
    if (api.soloLectura()) { api.avisarSoloLectura(); return; }
    const procesoId = paso === 'envio' ? obligacion.envio_id : obligacion.pago_id;
    const actual = cliente[paso]?.estado;
    const nuevo = siguienteEstadoObligacion(actual);
    const reemplazar = (valor) => setDatos(prev => prev.map(o => (o.clave !== obligacion.clave ? o : {
      ...o,
      clientes: o.clientes.map(c => (c.id !== cliente.id ? c : { ...c, [paso]: valor })),
    })));
    reemplazar({ estado: nuevo, fecha_actualizacion: new Date().toISOString(), usuario_nombre: null });
    try {
      await api.guardar(cliente.id, procesoId, nuevo, mes, anio);
      cargar();
    } catch (err) {
      reemplazar(cliente[paso]);
      avisarError('cambiar el estado', err);
    }
  };

  const celda = (obligacion, cliente, paso) => {
    const e = estado(cliente);
    const clave = e[paso];
    const esActual = e.pasoActual === paso;
    const dias = esActual ? e.dias : 0;
    const bloqueado = clave === 'bloqueado';
    const guardado = cliente[paso];
    const partes = [bloqueado ? 'Bloqueado: falta el envío al cliente' : NOMBRES_ESTADO[clave]];
    if (guardado && guardado.fecha_actualizacion && !bloqueado) {
      const quien = (guardado.usuario_nombre || '').trim().split(/\s+/)[0];
      partes.push(`actualizado el ${fechaCorta(guardado.fecha_actualizacion, hoy)}${quien ? ` por ${quien}` : ''}`);
    }
    if (dias) partes.push(`atrasado ${dias} ${dias === 1 ? 'día' : 'días'}`);
    const ayuda = partes.join(' · ');
    const nombrePaso = paso === 'envio' ? 'Envío al cliente' : 'Pago';
    return (
      <td className="cell-estado">
        <span className="estado-circulo-wrap">
          <button
            type="button"
            className={`estado-circle ${clave}${dias ? ' atrasado' : ''}`}
            data-tooltip={ayuda}
            aria-label={`${nombrePaso} de ${cliente.nombre}: ${ayuda}`}
            disabled={bloqueado}
            onClick={() => cambiar(obligacion, cliente, paso)}
          >
            <Icono clave={clave} />
          </button>
          {dias > 0 && <span className="atraso-badge" aria-hidden="true">+{dias}d</span>}
        </span>
      </td>
    );
  };

  if (datos === null) return <p className="obl-vacio">Cargando…</p>;

  const hayClientes = secciones.some(s => s.clientes.length);

  return (
    <div className="obligaciones">
      <div className="obl-herramientas">
        {!esContador && contadores.length > 1 && (
          <select className="obl-select" aria-label="Contador" value={contador} onChange={e => setContador(e.target.value)}>
            <option value="">Contador: todos</option>
            {contadores.map(([id, nombre]) => <option key={id} value={id}>{nombre}</option>)}
          </select>
        )}
        <label className="obl-buscar">
          <Search size={14} aria-hidden="true" />
          <input type="search" placeholder="Buscar cliente…" aria-label="Buscar cliente" value={busqueda} onChange={e => setBusqueda(e.target.value)} />
        </label>
        {(totalAtrasadas > 0 || filtroAtrasadas) && (
          <button
            type="button"
            className="filtro-chip atrasados"
            aria-pressed={filtroAtrasadas}
            onClick={() => setFiltroAtrasadas(v => !v)}
          >
            Atrasadas <span className="filtro-chip-num">{totalAtrasadas}</span>
          </button>
        )}
      </div>

      <div className="obl-elegir" role="group" aria-label="Obligación">
        <span>Obligación:</span>
        <button type="button" aria-pressed={elegida === null} onClick={() => setElegida(null)}>Todas</button>
        {secciones.map(s => (
          <button key={s.clave} type="button" aria-pressed={elegida === s.clave} onClick={() => setElegida(s.clave)}>
            {s.def.corto} <span className="obl-num">{s.toca ? s.todos.length : '—'}</span>
            {s.atrasados > 0 && <span className="obl-num-atraso" aria-label={`${s.atrasados} atrasados`}>{s.atrasados}</span>}
          </button>
        ))}
      </div>

      {!hayClientes && (
        <div className="obl-vacio">
          <b>Ningún cliente tiene obligaciones adicionales.</b> Se activan en Editar cliente → Obligaciones adicionales.
        </div>
      )}

      {hayClientes && secciones.filter(s => !elegida || s.clave === elegida).map(s => {
        if (!s.toca) {
          return (
            <section key={s.clave} className="obl-seccion no-toca">
              <div className="obl-cabecera"><h3>{s.def.nombre}</h3><span className="obl-no-toca">{textoNoToca(periodo, hoy)}</span></div>
            </section>
          );
        }
        if (filtroAtrasadas && !s.visibles.length && !elegida) return null;
        const cerrada = !elegida && cerradas[s.clave];
        return (
          <section key={s.clave} className={`obl-seccion${cerrada ? ' cerrada' : ''}`}>
            <div className="obl-cabecera">
              <button
                type="button"
                className="obl-plegar"
                aria-expanded={!cerrada}
                disabled={!!elegida}
                onClick={() => setCerradas(c => ({ ...c, [s.clave]: !c[s.clave] }))}
              >
                {!elegida && <ChevronDown size={16} aria-hidden="true" className="obl-flecha" />}
                <h3>{s.def.nombre}</h3>
              </button>
              <span className="obl-periodo">{textoPeriodo(s.def, mes)}</span>
              <span className="obl-limite">· a más tardar {fechaCorta(fechaLimiteObligacion({ mes, anio }), hoy)}</span>
              <span className="obl-avance">{textoAvance(s.todos, periodo, hoy)}</span>
            </div>
            {!cerrada && (s.visibles.length ? (
              <table className="obl-tabla">
                <thead>
                  <tr><th>Cliente</th><th className="col-proceso">Envío al cliente</th><th className="col-proceso">Pago</th></tr>
                </thead>
                <tbody>
                  {s.visibles.map(c => (
                    <tr key={c.id}>
                      <td className="obl-cliente">
                        {(() => {
                          const e = estado(c);
                          const terminados = (e.envio === 'completado' ? 1 : 0) + (e.pagado ? 1 : 0);
                          return (
                            <CeldaCliente
                              cliente={c}
                              onAbrir={() => onAbrirCliente && onAbrirCliente(c.id)}
                              onEditar={onEditarCliente ? () => onEditarCliente(c.id) : null}
                              mostrarContador={!contador}
                              avance={{ terminados, total: 2, ayuda: `${terminados} de 2 pasos terminados: envío al cliente y pago.` }}
                            />
                          );
                        })()}
                      </td>
                      {celda(s, c, 'envio')}
                      {celda(s, c, 'pago')}
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="obl-vacio">
                {s.clientes.length ? 'Ningún cliente coincide.' : `Ningún cliente tiene ${s.def.nombre}. Se activa en Editar cliente.`}
              </p>
            ))}
          </section>
        );
      })}

      {filtroAtrasadas && totalAtrasadas === 0 && (
        <p className="obl-vacio">Nada atrasado en {MESES[mes - 1]}.</p>
      )}
    </div>
  );
}
